import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminMediaItem, AdminGroup } from '@/lib/api'
import { useConfirm } from '@/components/ConfirmDialog'
import MediaCard from '@/components/MediaCard'

const CARD_GRID = 'grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-5 gap-4'

export default function MediaPage() {
  const { token, isGroupManager, managedGroupSlug } = useAuth()
  const navigate = useNavigate()

  const [items, setItems] = useState<AdminMediaItem[]>([])
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [activeTab, setActiveTab] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const [media, groupList] = await Promise.all([
        api.getAdminMedia(token),
        isGroupManager ? Promise.resolve([]) : api.getAdminGroups(token),
      ])
      setItems(media)
      setGroups(groupList)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [token, isGroupManager])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id: string, title: string) => {
    if (!await confirm(`"${title}" akan dihapus permanen dan tidak bisa dipulihkan.`, { title: `Hapus "${title}"` })) return
    if (!token) return
    try {
      await api.deleteMedia(token, id)
      load()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  const sortByDate = (list: AdminMediaItem[]) =>
    [...list].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  if (loading) return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  if (error) return <div className="p-6"><p className="text-red-400 text-sm">{error}</p></div>

  /* ── Group Manager: flat card grid ─────────────────────────── */
  if (isGroupManager) {
    const myItems = sortByDate(
      managedGroupSlug ? items.filter((m) => m.group_slug === managedGroupSlug) : items
    )

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg font-semibold text-white">Media</h1>
            <p className="text-zinc-500 text-xs mt-0.5">{myItems.length} items</p>
          </div>
          <button
            onClick={() => navigate('/media/create')}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            + Add Media
          </button>
        </div>

        {myItems.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-zinc-500 text-sm">No media items yet.</p>
            <button
              onClick={() => navigate('/media/create')}
              className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              + Add the first one
            </button>
          </div>
        ) : (
          <div className={CARD_GRID}>
            {myItems.map((item) => (
              <MediaCard
                key={item.id}
                item={item}
                onView={() => navigate(`/media/${item.id}`, { state: { media: item } })}
                onEdit={() => navigate(`/media/${item.id}/edit`, { state: { media: item } })}
                onDelete={() => handleDelete(item.id, item.title)}
              />
            ))}
          </div>
        )}
        {dialog}
      </div>
    )
  }

  /* ── Admin / Superadmin: tab bar + card grid ───────────────── */
  const tabs = [{ slug: '', name: 'Semua' }, ...groups.map((g) => ({ slug: g.slug, name: g.name }))]
  const filtered = sortByDate(
    activeTab ? items.filter((m) => m.group_slug === activeTab) : items
  )

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-semibold text-white">Media</h1>
          <p className="text-zinc-500 text-xs mt-0.5">{filtered.length} items</p>
        </div>
        <button
          onClick={() => navigate('/media/create')}
          className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
        >
          + Add Media
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-zinc-800 mb-6 overflow-x-auto scrollbar-none">
        {tabs.map((tab) => (
          <button
            key={tab.slug}
            type="button"
            onClick={() => setActiveTab(tab.slug)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
              activeTab === tab.slug
                ? 'text-white border-yellow-500'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-zinc-500 text-sm">No media items yet.</p>
          <button
            onClick={() => navigate('/media/create')}
            className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            + Add the first one
          </button>
        </div>
      ) : (
        <div className={CARD_GRID}>
          {filtered.map((item) => (
            <MediaCard
              key={item.id}
              item={item}
              onView={() => navigate(`/media/${item.id}`, { state: { media: item } })}
              onEdit={() => navigate(`/media/${item.id}/edit`, { state: { media: item } })}
              onDelete={() => handleDelete(item.id, item.title)}
            />
          ))}
        </div>
      )}
      {dialog}
    </div>
  )
}
